import React, { useState, useEffect, useRef, useMemo } from 'react';
import './FilterablePoolSelect.scss'; // Use the same styles

/**
 * Filterable company select component - used when there can be multiple companies.
 * Contains a search field and displays filtered results in a dropdown.
 */
export default function FilterableCompanySelect({ 
  companies = [], 
  value, 
  onChange, 
  placeholder = "Valitse yritys...",
  emptyOptionLabel = "Ei yritystä",
  disabled = false,
  required = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Find the selected company for displaying the name
  const selectedCompany = useMemo(() => {
    if (!value) return null;
    return companies.find(c => c.id === parseInt(value) || c.id === value);
  }, [companies, value]);

  // Suodata yritykset hakutermin perusteella
  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) return companies;
    const term = searchTerm.toLowerCase();
    return companies.filter(company => 
      company.name?.toLowerCase().includes(term)
    );
  }, [companies, searchTerm]);

  // Sulje dropdown kun klikataan ulkopuolella
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.pool-option');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setHighlightedIndex(-1);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleSelectCompany = (companyId) => {
    onChange(companyId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    // +1 for empty option if not required
    const totalOptions = required ? filteredCompanies.length : filteredCompanies.length + 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : totalOptions - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (!required) {
          if (highlightedIndex === 0) {
            handleSelectCompany('');
          } else if (highlightedIndex > 0 && filteredCompanies[highlightedIndex - 1]) {
            handleSelectCompany(filteredCompanies[highlightedIndex - 1].id);
          }
        } else {
          if (highlightedIndex >= 0 && filteredCompanies[highlightedIndex]) {
            handleSelectCompany(filteredCompanies[highlightedIndex].id);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const displayValue = isOpen 
    ? searchTerm 
    : (selectedCompany ? selectedCompany.name : '');

  return (
    <div 
      className={`filterable-pool-select ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
      ref={containerRef}
    >
      <div className="select-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="form-input pool-search-input"
          value={displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        <span className="select-arrow" onClick={() => !disabled && setIsOpen(!isOpen)}>
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path fill="#333" d="M6 9L1 4h10z" />
          </svg>
        </span>
        {selectedCompany && !isOpen && !required && (
          <button 
            type="button"
            className="clear-button"
            onClick={(e) => {
              e.stopPropagation();
              handleSelectCompany('');
            }}
            disabled={disabled}
            aria-label="Tyhjennä valinta"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div className="pool-dropdown-menu" ref={listRef}>
          {/* "No company" option only if not required */}
          {!required && (
            <div
              className={`pool-option pool-option-empty ${highlightedIndex === 0 ? 'highlighted' : ''} ${!value ? 'selected' : ''}`}
              onClick={() => handleSelectCompany('')}
            >
              {emptyOptionLabel}
            </div>
          )}

          {filteredCompanies.length === 0 ? (
            <div className="pool-option-no-results">
              Ei tuloksia haulle "{searchTerm}"
            </div>
          ) : (
            filteredCompanies.map((company, index) => {
              const optionIndex = required ? index : index + 1;
              return (
                <div
                  key={company.id}
                  className={`pool-option ${highlightedIndex === optionIndex ? 'highlighted' : ''} ${value === company.id || value === String(company.id) ? 'selected' : ''}`}
                  onClick={() => handleSelectCompany(company.id)}
                >
                  {company.name}
                </div>
              );
            })
          )}

          {companies.length > 10 && (
            <div className="pool-count-info">
              Näytetään {filteredCompanies.length} / {companies.length} yritystä
            </div>
          )}
        </div>
      )}
    </div>
  );
}

